import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { User, AuthState } from '../chat/chat.types';
import * as authService from '../../services/authService';
import type { SignupData } from '../../services/authService';

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
  isAMSUser: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authService.getCurrentUser().then((restored) => {
      if (restored) setUser(restored);
      setIsLoading(false);
    });
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const loggedIn = await authService.login(email, password);
      setUser(loggedIn);
      const amsToken = localStorage.getItem('amsToken') || '';
      if ((process.env.REACT_APP_Auth_Url || '')) {
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/account/toolboxtoken?nctoken=${encodeURIComponent(amsToken)}`)
          .then(r => r.json())
          .then(t => { if (t?.token) localStorage.setItem('toolboxToken', t.token); })
          .catch(() => { /* silent */ });
      }

      window.dispatchEvent(new CustomEvent('authTokenUpdated'));
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signup = useCallback(async (data: SignupData) => {
    setIsLoading(true);
    setError(null);
    try {
      const newUser = await authService.signup(data);
      setUser(newUser);
      window.dispatchEvent(new CustomEvent('authTokenUpdated'));
    } catch (err: any) {
      setError(err.message || 'Signup failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    clearError,
    login,
    signup,
    logout,
    isAMSUser: user?.isAMSUser ?? false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

